import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import PrintIcon from '@mui/icons-material/Print';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import last from 'lodash/last';
import PopupState, {bindMenu, bindTrigger} from 'material-ui-popup-state';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useGetMinerNetworkId} from '../../../hooks/get-miner-network-id';
import {usePrint} from '../../../hooks/print';
import {useAppDispatch, useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import useShiftSelect, {isShiftClick} from '../../../hooks/useShiftSelect';
import {
  CommtracNodeByCnEvent,
  CommtracNodeByCnEventListQuery,
  CommtracNodeByCnEventListResponse,
} from '../../../interfaces/CommtracNodeByCnEvent';
import reduxActions from '../../../redux/actions';
import {getBooleanValue} from '../../../utils/boolean';
import {employeeMessageTypes} from '../../../utils/message-types';
import {
  PRINT_HIDDEN_SX,
  PRINTABLE_CONTAINER_SX,
  PRINTABLE_MAP_SX,
} from '../../../utils/printStyles';
import {
  handleChangeTab,
  handleCloseTab,
  handleOpenTab,
} from '../../../utils/tab';
import {AdaptiveButtonGroup} from '../../common/AdaptiveButtonGroup';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {Map, MapLayerId} from '../../common/Map';
import {ResizableColumns} from '../../common/ResizableColumns';
import TabLabel from '../../common/TabLabel';
import TruncatedText from '../../common/TruncantedText';
import ObjectsByCommunicationNodeTrackingExportExcelButton from '../../communication-node/ObjectsByCommunicationNodeTrackingExportExcelButton';
import {DateTimeRangeSelect} from '../../selectors/DateTimeRangeSelect';
import MessageTypeSelect from '../../selectors/MessageTypeSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';

interface Props {
  value?: CommtracNodeByCnTrackingReportsData;
  onUpdate?: (value?: CommtracNodeByCnTrackingReportsData) => void;
}

const DEFAULT_SHOWN_FIELDS = [
  'battery_voltage',
  'id',
  'nickname',
  'first_name',
  'last_name',
  'commtrac_external_id',
  'date',
  'message_type',
  'motion_sensor_value',
  'answer',
  'commtrac_current_zone_name',
  'strongest_cn_name',
];

interface CommtracNodeByCnTrackingReportsDataTab {
  id: number;
  refreshInterval?: number | null;
  selectedIds?: number[];
  selectAll: boolean;
  params?: {
    date_start?: string | null;
    date_end?: string | null;
    by_strongest_cn_only?: boolean;
    event_type?: number[];
    asset_type?: ('miner' | 'asset')[];
    page?: number;
    limit?: number;
    order?: string | null;
    dir?: 'ASC' | 'DESC';
  };
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export interface CommtracNodeByCnTrackingReportsData {
  activeId?: number;
  mapLayers: MapLayerId[];
  mapLevel: number | null;
  openedItems: CommtracNodeByCnTrackingReportsDataTab[];
}

export const getCommtracNodeByCnTrackingReportsData =
  (): CommtracNodeByCnTrackingReportsData => ({
    activeId: undefined,
    openedItems: [],
    mapLayers: ['mine', 'commtrac_node_by_cn_history'],
    mapLevel: null,
  });

const getCommtracNodeByCnTrackingReportsDataTab = (
  id: number
): CommtracNodeByCnTrackingReportsDataTab => ({
  id,
  selectAll: true,
  params: {
    date_start: dayjs().format('YYYY-MM-DD 00:00:00'),
    date_end: dayjs().format('YYYY-MM-DD 23:59:59'),
  },
  columnWidths: {},
  columnOrder: undefined,
  shownFields: DEFAULT_SHOWN_FIELDS,
});

const TYPE_OPTIONS = [
  {value: 'miner', label: 'Employees'},
  {value: 'asset', label: 'Assets'},
];

export const CommtracNodeByCnTrackingReports = ({value, onUpdate}: Props) => {
  const reduxDispatch = useAppDispatch();

  const config = useMemo(
    () => ({
      ...(value ?? getCommtracNodeByCnTrackingReportsData()),
      mapLayers:
        value?.mapLayers ?? getCommtracNodeByCnTrackingReportsData().mapLayers,
    }),
    [value]
  );

  const isOpenAwayFromConnectView = useMemo(() => {
    return (
      location.pathname.includes('/panels/') ||
      !document.getElementById('connect-view-panel')
    );
  }, [location, value]);

  const openedItemIndex = useMemo(
    () =>
      (config.activeId
        ? config.openedItems.findIndex((i) => i.id === config.activeId)
        : null) ?? config.openedItems.length - 1,
    [config.activeId, config.openedItems]
  );

  const openedItem = useMemo(
    () =>
      openedItemIndex !== -1
        ? config.openedItems[openedItemIndex]
          ? config.openedItems[openedItemIndex]
          : getCommtracNodeByCnTrackingReportsDataTab(config.activeId as number)
        : null,
    [openedItemIndex, config.openedItems]
  );

  const nodes = useAppSelector(({assets}) =>
    assets.nodes.filter(
      (i) => i.node_type === 'communication_node' && i.ack === '1'
    )
  );

  const tabNames = useMemo(
    () =>
      config.openedItems.map(
        (o) => nodes.find((i) => i.cn_external_id === o.id)?.name
      ),
    [config.openedItems, nodes]
  );

  // get fresh assets
  useEffect(() => {
    reduxDispatch(reduxActions.assets.fetchNodes);
  }, [config.openedItems]);

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] =
    useState<CommtracNodeByCnEventListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo<CommtracNodeByCnEventListQuery | null>(
    () =>
      openedItem?.id
        ? {
            commtrac_external_id: openedItem?.id,
            date_start:
              openedItem.params?.date_start ??
              dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            date_end:
              openedItem.params?.date_end ??
              dayjs()
                .startOf('day')
                .add(1, 'day')
                .format('YYYY-MM-DD HH:mm:ss'),
            event_type: openedItem.params?.event_type ?? [],
            asset_type: openedItem.params?.asset_type ?? [],
            by_strongest_cn_only:
              openedItem.params?.by_strongest_cn_only ?? true,
            page: openedItem.params?.page ?? 0,
            limit: openedItem.params?.limit ?? 25,
            order: openedItem.params?.order ?? null,
            dir: openedItem.params?.dir ?? 'DESC',
          }
        : null,
    [openedItem?.id, openedItem?.params]
  );

  const fetchData = useCallback(
    async (params: CommtracNodeByCnEventListQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const resp = await API.get<CommtracNodeByCnEventListResponse>(
          `${apiBaseUrl}/communication-node/events`,
          {params}
        );
        setFetchedData(resp.data);
        // collect all IDs
        if (openedItem?.selectAll) {
          openedItem.selectedIds = resp.data.items.map((el) => el.id);
        }
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_f1aa67fb');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  useEffect(() => {
    if (params) {
      fetchData(params);
    }
  }, [params]);

  useEffect(() => {
    if (!openedItem) {
      setFetchedData(undefined);
    }
  }, [openedItem]);

  /****************/
  /* auto refresh */
  /****************/
  const callFetchData = useCallback(() => {
    params && fetchData(params);
  }, [params]);
  useRefreshInterval(callFetchData, openedItem?.refreshInterval);

  /*************/
  /* data grid */
  /*************/
  const getMinerNetworkId = useGetMinerNetworkId();
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<CommtracNodeByCnEvent>[] = [
    {
      field: 'select',
      type: 'select',
      width: 40,
      hideable: false,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedItems.length > 0 && selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row, visibleRows}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(row.id)}
          onChange={(event) =>
            toggleSelectItem(
              row.id,
              isShiftClick(event),
              visibleRows.map((visibleRow) => visibleRow.id)
            )
          }
        />
      ),
    },
    {
      headerName: 'Battery Voltage',
      field: 'battery_voltage',
      renderHeader: () => (
        <Tooltip title="Battery Level">
          <BatteryChargingFullIcon />
        </Tooltip>
      ),
      sortable: false,
      width: 50,
      valueGetter: ({row}) => {
        return row.battery_voltage ? row.battery_voltage / 10 : null;
      },
      renderCell: ({value}) => {
        if (value === null) {
          return <QuestionMarkIcon fontSize="small" />;
        }
        return <>{value}</>;
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 50,
      resizeable: true,
      sortable: true,
    },
    {
      headerName: 'Nickname',
      field: 'nickname',
      width: 150,
      resizeable: true,
      sortable: true,
      renderCell: ({row, value}) => {
        return (
          <TruncatedText
            color={getBooleanValue(row.alarm, 0, 1) ? 'error.main' : undefined}
            fontWeight={getBooleanValue(row.alarm, 0, 1) ? 'bold' : undefined}
          >
            {value?.toString()}
          </TruncatedText>
        );
      },
    },
    {
      field: 'first_name',
      headerName: 'First Name',
      width: 150,
      resizeable: true,
      sortable: true,
      renderCell: ({row, value}) => {
        return (
          <TruncatedText
            color={getBooleanValue(row.alarm, 0, 1) ? 'error.main' : undefined}
            fontWeight={getBooleanValue(row.alarm, 0, 1) ? 'bold' : undefined}
          >
            {value?.toString()}
          </TruncatedText>
        );
      },
    },
    {
      field: 'commtrac_external_id',
      width: 120,
      resizeable: true,
      headerName: 'Network ID',
      sortable: false,
      valueGetter: ({row}) => {
        if (row.commtrac_external_id) {
          if (row.type === 2) {
            return getMinerNetworkId(row.commtrac_external_id);
          }
          return row.commtrac_external_id;
        }
      },
      renderCell: ({row, value}) => {
        return (
          <TruncatedText
            color={getBooleanValue(row.alarm, 0, 1) ? 'error.main' : undefined}
            fontWeight={getBooleanValue(row.alarm, 0, 1) ? 'bold' : undefined}
          >
            {value?.toString()}
          </TruncatedText>
        );
      },
    },
    {
      field: 'date',
      headerName: 'Timestamp',
      width: 180,
      resizeable: true,
      sortable: true,
      renderCell: ({row, value}) => {
        return (
          <TruncatedText
            color={getBooleanValue(row.alarm, 0, 1) ? 'error.main' : undefined}
            fontWeight={getBooleanValue(row.alarm, 0, 1) ? 'bold' : undefined}
          >
            {value?.toString()}
          </TruncatedText>
        );
      },
    },
    {
      field: 'message_type',
      width: 120,
      headerName: 'Message Type',
      resizeable: true,
      sortable: true,
      renderCell: ({row, value}) => {
        return (
          <TruncatedText
            color={getBooleanValue(row.alarm, 0, 1) ? 'error.main' : undefined}
            fontWeight={getBooleanValue(row.alarm, 0, 1) ? 'bold' : undefined}
          >
            {value?.toString()}
          </TruncatedText>
        );
      },
    },
    {
      field: 'motion_sensor_value',
      headerName: 'Motion State',
      width: 130,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'answer',
      headerName: 'Status',
      width: 100,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'commtrac_current_zone_name',
      headerName: 'Section',
      width: 100,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'strongest_cn_name',
      headerName: 'Strongest Node',
      width: 130,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'pos_lon',
      headerName: 'Longtitude',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'pos_lat',
      headerName: 'Latitude',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'beacon_data',
      width: 180,
      resizeable: true,
      headerName: 'Beacon Data',
    },
  ];

  const shownFields = useMemo(
    () => openedItem?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [openedItem?.shownFields]
  );

  const exportFields = useMemo(() => {
    return columns
      .filter((col) => !!col.headerName)
      .map((col) => ({
        field: col.field,
        label: col.headerName,
        hidden: !shownFields?.includes(col.field),
      }));
  }, [shownFields]);

  const dataGridRef = useRef<DataGridRef>(null);
  const printContainerRef = useRef<HTMLDivElement>(null);
  const [printReport] = usePrint(printContainerRef.current);

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = openedItem?.selectedIds ?? [];

  const selectedRows = useMemo(
    () => rows.filter((i) => selectedItems?.includes(i.id)),
    [rows, selectedItems]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const getNextSelectedIds = useShiftSelect<number>();

  const toggleSelectItem = (
    id: number,
    shiftKey: boolean,
    visibleIds: number[]
  ) => {
    if (openedItem) {
      const nextSelectedIds = getNextSelectedIds({
        id,
        visibleIds,
        selectedIds: selectedItems ?? [],
        shiftKey,
      });
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              $set: {
                ...openedItem,
                selectedIds: nextSelectedIds,
                selectAll: nextSelectedIds.length === fetchedData?.items.length,
              },
            },
          },
        })
      );
    }
  };

  const selectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectedIds: {
                $set: rows?.map((i) => i.id) ?? [],
              },
              selectAll: {
                $set: true,
              },
            },
          },
        })
      );
    }
  };

  const unselectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectedIds: {
                $set: [],
              },
              selectAll: {
                $set: false,
              },
            },
          },
        })
      );
    }
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  const openTab = (id: number) => {
    return handleOpenTab(id, config, onUpdate);
  };

  const closeTab = (id: number) => {
    return handleCloseTab(id, config, onUpdate);
  };

  const changeTab = (_: any, id: number) => {
    handleChangeTab(id, config, onUpdate);
  };

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              $set: {
                ...openedItem,
                columnWidths: widths,
              },
            },
          },
        })
      );
    }
  };

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [onUpdate, config, openedItemIndex, openedItem]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                columnOrder: {$set: order},
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [onUpdate, config, openedItemIndex, openedItem]
  );

  return (
    <>
      <DashboardPanelTitleSlot>
        Objects by CN Tracking Reports
      </DashboardPanelTitleSlot>

      <Box ref={printContainerRef} sx={PRINTABLE_CONTAINER_SX}>
        <ResizableColumns
          left={
            <Map
              sx={PRINTABLE_MAP_SX}
              panel="cn_tracking_report"
              selectedMapLayers={config.mapLayers}
              selectedLevel={config.mapLevel}
              commtracNodeByCnEventsData={selectedRows}
              minWidth={250}
              availableMapLayers={[
                'street',
                'mine',
                'commtrac_node_by_cn_history',
                'nodes',
                'alarms',
              ]}
              onSelectMapLayers={(v) => {
                onUpdate?.(
                  update(config, {
                    mapLayers: {
                      $set: v,
                    },
                  })
                );
              }}
              onSelectLevel={(v) => {
                onUpdate?.(
                  update(config, {
                    mapLevel: {
                      $set: v,
                    },
                  })
                );
              }}
            />
          }
        >
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            width="100%"
            overflow="hidden"
          >
            <Box
              sx={PRINT_HIDDEN_SX}
              display="flex"
              flexDirection="column"
              gap={3}
              py={1.5}
              bgcolor={(theme) =>
                theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF'
              }
            >
              {isOpenAwayFromConnectView && (
                <Box pr={1.5}>
                  <PopupState variant="popover" popupId="demo-popup-menu">
                    {(popupState) => (
                      <>
                        <Button variant="outlined" {...bindTrigger(popupState)}>
                          Select an Object
                        </Button>
                        <Menu {...bindMenu(popupState)}>
                          {nodes?.map((it) => (
                            <MenuItem
                              key={it.id}
                              onClick={() => {
                                it?.cn_external_id &&
                                  openTab(it.cn_external_id);
                                popupState.close();
                              }}
                            >
                              {it.name}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </PopupState>
                </Box>
              )}
              {config.openedItems.length ? (
                <Box>
                  <Tabs
                    value={openedItem?.id}
                    variant="scrollable"
                    onChange={changeTab}
                  >
                    {config.openedItems.map((i, idx) => (
                      <Tab
                        key={i.id}
                        value={i.id}
                        label={
                          <TabLabel
                            name={tabNames?.[idx] ?? 'Undefined'}
                            onClose={() => closeTab(i.id)}
                          />
                        }
                      />
                    ))}
                  </Tabs>
                </Box>
              ) : (
                <Box minWidth={400} pr={1.5}>
                  <Alert severity="warning">
                    No Communication Nodes Opened
                  </Alert>
                </Box>
              )}

              <Box display="flex" flexDirection="column" pr={1.5}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Box
                    display="flex"
                    gap={1}
                    sx={{flex: '1 1 auto', minWidth: 0}}
                  >
                    <Box
                      sx={{
                        minWidth: 300,
                        maxWidth: 400,
                        flex: '1 1 300px',
                        flexShrink: 1,
                      }}
                    >
                      <DateTimeRangeSelect
                        value={
                          openedItem
                            ? [
                                dayjs(params?.date_start).toDate(),
                                dayjs(params?.date_end).toDate(),
                              ]
                            : undefined
                        }
                        size="small"
                        disabled={!openedItem}
                        onChange={(v) => {
                          if (openedItem) {
                            onUpdate?.(
                              update(config, {
                                openedItems: {
                                  [openedItemIndex]: {
                                    $set: {
                                      ...openedItem,
                                      selectAll: true,
                                      params: {
                                        ...params,
                                        date_start: v?.[0]
                                          ? dayjs(v?.[0]).format(
                                              'YYYY-MM-DD HH:mm:ss'
                                            )
                                          : undefined,
                                        date_end: v?.[0]
                                          ? dayjs(v?.[1]).format(
                                              'YYYY-MM-DD HH:mm:ss'
                                            )
                                          : undefined,
                                      },
                                    },
                                  },
                                },
                              })
                            );
                          }
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        minWidth: 120,
                        maxWidth: 180,
                        flex: '1 1 120px',
                        flexShrink: 1,
                      }}
                    >
                      <MessageTypeSelect
                        value={params?.event_type}
                        size="small"
                        disabled={!openedItem}
                        options={employeeMessageTypes.filter((i) =>
                          [1, 12].includes(i.code)
                        )}
                        onChange={(v) => {
                          if (openedItem) {
                            onUpdate?.(
                              update(config, {
                                openedItems: {
                                  [openedItemIndex]: {
                                    $set: {
                                      ...openedItem,
                                      selectAll: true,
                                      params: {
                                        ...openedItem.params,
                                        event_type: v?.length ? v : undefined,
                                      },
                                    },
                                  },
                                },
                              })
                            );
                          }
                        }}
                      />
                    </Box>

                    <Box
                      sx={{
                        minWidth: 90,
                        maxWidth: 150,
                        flex: '1 1 90px',
                        flexShrink: 1,
                      }}
                    >
                      <TextField
                        value={
                          !params?.asset_type?.length
                            ? ['all']
                            : params.asset_type
                        }
                        disabled={!openedItem}
                        label="Type"
                        fullWidth
                        size="small"
                        select
                        SelectProps={{multiple: true}}
                        onChange={(e) => {
                          const v = e.target.value as unknown as (
                            | 'miner'
                            | 'asset'
                            | 'all'
                          )[];
                          const newValue =
                            last(v) === 'all'
                              ? undefined
                              : v.filter((i) => i !== 'all');
                          if (openedItem) {
                            onUpdate?.(
                              update(config, {
                                openedItems: {
                                  [openedItemIndex]: {
                                    $set: {
                                      ...openedItem,
                                      selectAll: true,
                                      params: {
                                        ...openedItem.params,
                                        asset_type: newValue as any,
                                      },
                                    },
                                  },
                                },
                              })
                            );
                          }
                        }}
                      >
                        <MenuItem value="all">Both</MenuItem>
                        {TYPE_OPTIONS.map((i) => (
                          <MenuItem key={i.value} value={i.value}>
                            {i.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>

                    <Box
                      sx={{
                        minWidth: 80,
                        maxWidth: 140,
                        flex: '1 1 80px',
                        flexShrink: 1,
                      }}
                    >
                      <FormControlLabel
                        label="Strongest CN"
                        control={
                          <Checkbox
                            size="small"
                            checked={params?.by_strongest_cn_only ?? true}
                          />
                        }
                        disabled={!openedItem}
                        sx={{
                          minWidth: 80,
                          maxWidth: 140,
                          width: '100%',
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                        onChange={(_e, v) => {
                          if (openedItem) {
                            onUpdate?.(
                              update(config, {
                                openedItems: {
                                  [openedItemIndex]: {
                                    $set: {
                                      ...openedItem,
                                      selectAll: true,
                                      params: {
                                        ...openedItem.params,
                                        by_strongest_cn_only: v,
                                      },
                                    },
                                  },
                                },
                              })
                            );
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  <Box display="flex" sx={{flex: 1, minWidth: 0}}>
                    <Box sx={{flex: 1, minWidth: 0}}>
                      <AdaptiveButtonGroup disabled={!openedItem}>
                        <Button size="small" data-priority={1}>
                          <ObjectsByCommunicationNodeTrackingExportExcelButton
                            componentProps={{
                              ...params,
                              node_name: tabNames[openedItemIndex],
                            }}
                            exportFields={exportFields}
                          />
                        </Button>

                        <Button
                          size="small"
                          onClick={() => params && fetchData(params)}
                          data-priority={2}
                        >
                          <RefreshIcon />
                        </Button>

                        <AutoRefreshSelect
                          value={openedItem?.refreshInterval ?? null}
                          onChange={(v) => {
                            if (openedItem) {
                              onUpdate?.(
                                update(config, {
                                  openedItems: {
                                    [openedItemIndex]: {
                                      $set: {
                                        ...openedItem,
                                        refreshInterval: v,
                                      },
                                    },
                                  },
                                })
                              );
                            }
                          }}
                          data-priority={3}
                        />

                        <Button
                          size="small"
                          onClick={() => printReport()}
                          data-priority={4}
                        >
                          <PrintIcon />
                        </Button>
                      </AdaptiveButtonGroup>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Backdrop
              open={fetchedInProgress}
              sx={{position: 'absolute', zIndex: 1199}}
            >
              <CircularProgress color="inherit" />
            </Backdrop>

            {fetchedErrors.map((error, idx) => (
              <Alert
                key={idx}
                severity="error"
                onClose={() => params && fetchData(params)}
              >
                {error}
              </Alert>
            ))}

            <DataGrid
              ref={dataGridRef}
              rows={rows}
              columns={columns}
              loading={fetchedInProgress}
              shownFields={shownFields}
              defaultShownFields={DEFAULT_SHOWN_FIELDS}
              columnWidths={openedItem?.columnWidths}
              columnOrder={openedItem?.columnOrder}
              pagination
              paginationMode="server"
              size="small"
              sortBy={
                params?.order
                  ? {
                      field: params?.order,
                      dir: params?.dir === 'DESC' ? 'desc' : 'asc',
                    }
                  : null
              }
              sortingMode="server"
              page={params?.page}
              pageSize={params?.limit}
              rowCount={fetchedData?.count}
              sxFooter={{
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
              }}
              onPageChange={(v) => {
                if (openedItem) {
                  onUpdate?.(
                    update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: {
                            ...openedItem,
                            selectAll: true,
                            params: {
                              ...openedItem.params,
                              page: v,
                            },
                          },
                        },
                      },
                    })
                  );
                }
              }}
              onPageSizeChange={(v) => {
                if (openedItem) {
                  onUpdate?.(
                    update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: {
                            ...openedItem,
                            selectAll: true,
                            params: {
                              ...openedItem.params,
                              page: 0,
                              limit: v,
                            },
                          },
                        },
                      },
                    })
                  );
                }
              }}
              onSort={(v) => {
                if (v && openedItem) {
                  onUpdate?.(
                    update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: {
                            ...openedItem,
                            params: {
                              ...openedItem.params,
                              order: v.field,
                              dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                            },
                          },
                        },
                      },
                    })
                  );
                }
              }}
              onShownFieldsChange={handleShownFieldsChange}
              onColumnWidthsChange={handleColumnWidthChange}
              onColumnOrderAndShownFieldsChange={
                handleColumnOrderAndShownFieldsChange
              }
            />
          </Box>
        </ResizableColumns>
      </Box>
    </>
  );
};
